import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './WriterPublish.css';
import RichTextEditor from './RichTextEditor';
import { extractThumbnailSuggestions } from '../utils/thumbnailSuggestions';
import { isRichTextEmpty, normalizeRichTextContent } from '../utils/richText';
import ThumbnailPicker from './ThumbnailPicker';
import TagGroupInput from './TagGroupInput';
import VideoLinkField from './VideoLinkField';
import PublishDestination, { DESTINATION_ARTICLES, DESTINATION_VIDEOS } from './PublishDestination';
import { normalizeTagValue } from '../utils/tagUtils';
import { uploadMediaFile } from '../utils/directUpload';
import {
    VIDEO_NOT_STORED_MESSAGE,
    VIDEO_URL_ERROR,
    getVideoLinkKey,
    isVideoPost,
    parseVideoUrl,
} from '../utils/video';

const WriterPublish = ({
    onPublished,
    defaultPassphrase = '',
    defaultExpanded = false,
    inline = false,
    adminToken = '',
    adminAuthorHash = '',
    tagGroups = [],
}) => {
    const isAdmin = typeof adminToken === 'string' && adminToken.trim().length > 0;
    const [formData, setFormData] = useState({
        passphrase: defaultPassphrase,
        alias: '',
        title: '',
        description: '',
        content: '',
        bibliography: '',
        thumbnail: '',
        destination: DESTINATION_ARTICLES,
        videoUrl: '',
        tagGroups: [],
        confidenceScoreRating: '3'
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(defaultExpanded || inline);
    const tagGroupsRef = useRef([]);
    // Title/channel fetched for the video link preview; sent along as a
    // fallback for when the backend can't reach the platform itself.
    const [videoMeta, setVideoMeta] = useState(null);

    const resetForm = useCallback(() => {
        tagGroupsRef.current = [];
        setFormData({
            passphrase: defaultPassphrase,
            alias: '',
            title: '',
            description: '',
            content: '',
            bibliography: '',
            thumbnail: '',
            destination: DESTINATION_ARTICLES,
            videoUrl: '',
            tagGroups: [],
            confidenceScoreRating: '3'
        });
        setVideoMeta(null);
        setError('');
    }, [defaultPassphrase]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            passphrase: defaultPassphrase
        }));
    }, [defaultPassphrase]);

    useEffect(() => {
        if (defaultExpanded || inline) {
            setShowForm(true);
        }
    }, [defaultExpanded, inline]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleContentChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            content: nextValue,
        }));
    };

    const handleBibliographyChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            bibliography: nextValue,
        }));
    };

    const handleTagGroupsChange = (nextValue) => {
        const nextGroups = Array.isArray(nextValue) ? nextValue : [];
        tagGroupsRef.current = nextGroups;
        setFormData((prev) => ({
            ...prev,
            tagGroups: nextGroups,
        }));
    };

    // The editors and the thumbnail picker report upload failures
    // themselves, next to the upload (a per-file summary; a failed automatic
    // video poster is skipped silently), so uploads leave the form-level
    // error alone.
    const handleMediaUpload = async (file) => {
        const trimmedPassphrase = formData.passphrase?.trim();
        if (!isAdmin && !trimmedPassphrase) {
            throw new Error('Writer passphrase is required before uploading media');
        }
        return uploadMediaFile(file, { adminToken, passphrase: trimmedPassphrase });
    };

    const handleThumbnailChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            thumbnail: nextValue,
        }));
    };

    const handleDestinationChange = (nextDestination) => {
        setFormData((prev) => ({
            ...prev,
            destination: nextDestination,
        }));
    };

    const handleVideoUrlChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            videoUrl: nextValue,
        }));
    };

    // The video's own title becomes the document title unless the writer
    // already typed one.
    const handleVideoPreview = useCallback((meta) => {
        setVideoMeta(meta);
        if (meta?.title) {
            setFormData((prev) => (
                prev.title.trim() ? prev : { ...prev, title: meta.title.slice(0, 200) }
            ));
        }
    }, []);

    const isVideoDestination = formData.destination === DESTINATION_VIDEOS;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const normalizedContent = normalizeRichTextContent(formData.content);
        const normalizedBibliography = normalizeRichTextContent(formData.bibliography);

        if (isRichTextEmpty(normalizedContent)) {
            setError('Full Content is empty');
            return;
        }

        // Condoned Media posts need a playable link; articles send none.
        let videoPayload = {};
        if (isVideoDestination) {
            const trimmedVideoUrl = (formData.videoUrl || '').trim();
            const parsedVideo = parseVideoUrl(trimmedVideoUrl);
            if (!trimmedVideoUrl) {
                setError('Paste the YouTube or Rumble link of the video for the Condoned Media page');
                return;
            }
            if (!parsedVideo) {
                setError(VIDEO_URL_ERROR);
                return;
            }
            videoPayload = {
                videoUrl: trimmedVideoUrl,
                ...(videoMeta && videoMeta.key === getVideoLinkKey(parsedVideo)
                    ? { videoTitle: videoMeta.title, videoChannel: videoMeta.channel }
                    : {}),
            };
        }

        if (isAdmin && !String(adminAuthorHash || '').trim()) {
            setError('Select an author before publishing in admin mode');
            return;
        }
        const sourceTagGroups = (Array.isArray(tagGroupsRef.current) && tagGroupsRef.current.length > 0)
            ? tagGroupsRef.current
            : (Array.isArray(formData.tagGroups) ? formData.tagGroups : []);

        const tagGroupPayload = sourceTagGroups
            .map((entry) => {
                const groupId = typeof entry?.groupId === 'string'
                    ? entry.groupId.trim()
                    : (typeof entry?.id === 'string' ? entry.id.trim() : '');
                const tags = Array.isArray(entry?.tags) ? entry.tags : [];
                const normalizedTags = tags
                    .map((tag) => normalizeTagValue(tag))
                    .filter(Boolean)
                    .filter((tag, index, array) => array.indexOf(tag) === index);
                return { groupId, tags: normalizedTags };
            })
            .filter((entry) => entry.groupId && Array.isArray(entry.tags) && entry.tags.length > 0);

        const flattenedTags = tagGroupPayload.flatMap((entry) => entry.tags);
        const uniqueFlattenedTags = [...new Set(flattenedTags)];

        if (uniqueFlattenedTags.length === 0) {
            if (process.env.NODE_ENV !== 'production') {
                console.debug('[WriterPublish] No tags detected. sourceTagGroups=', sourceTagGroups);
                console.debug('[WriterPublish] Computed tagGroupPayload=', tagGroupPayload);
            }
            setError('Select at least one sub-tag under a main tag');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const trimmedThumbnail = formData.thumbnail?.trim() || '';

            const response = await axios.post(
                `${API_URL}/api/posts`,
                {
                    ...(isAdmin ? { authorHash: String(adminAuthorHash || '').trim() } : { passphrase: formData.passphrase }),
                    alias: formData.alias,
                    title: formData.title,
                    description: formData.description,
                    thumbnail: trimmedThumbnail,
                    content: normalizedContent,
                    confidenceScoreRating: Number(formData.confidenceScoreRating),
                    bibliography: isRichTextEmpty(normalizedBibliography) ? '' : normalizedBibliography,
                    tagGroups: tagGroupPayload,
                    ...videoPayload,
                },
                isAdmin ? { headers: { Authorization: `Bearer ${adminToken.trim()}` } } : undefined
            );

            // Clear form
            resetForm();

            setShowForm(inline ? true : defaultExpanded);
            
            if (onPublished) {
                onPublished(response.data);
            }

            // Report where the server actually filed it: a backend without
            // Condoned Media support publishes the document but drops the link.
            if (isVideoDestination && !isVideoPost(response.data)) {
                alert(`[Published — video link not saved] ${VIDEO_NOT_STORED_MESSAGE}`);
            } else {
                alert(isVideoDestination
                    ? '[Published Successfully] Listed on the Condoned Media — Videos page (/videos).'
                    : '[Published Successfully] Listed under Articles on the front page.');
            }
        } catch (err) {
            setError(err.response?.data?.message || '[Transmission Failed]');
        } finally {
            setLoading(false);
        }
    };

    if (!inline && !showForm && !defaultExpanded) {
        return (
            <button 
                className="writer-toggle-button"
                onClick={() => setShowForm(true)}
            >
                + Publish
            </button>
        );
    }

    return (
        <div className={`writer-publish-container${inline ? ' inline' : ''}`}>
            <div className={`writer-publish-card${inline ? ' inline' : ''}`}>
                <div className="card-header">
                    <h2>[Creating a Document]</h2>
                    {!inline && (
                        <button 
                            className="close-button"
                            onClick={() => setShowForm(false)}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="writer-form">
                    {error && (
                        <div className="error-message">
                            [ERROR] {error}
                        </div>
                    )}

                    <div className="form-section">
                        {isAdmin ? (
                            <>
                                <label>ADMIN MODE</label>
                                <small>Publishing as selected author.</small>
                            </>
                        ) : (
                            <>
                                <label>WRITER PASSPHRASE *</label>
                                <input
                                    type="password"
                                    name="passphrase"
                                    value={formData.passphrase}
                                    onChange={handleChange}
                                    placeholder="Enter your writer passphrase..."
                                    required
                                    autoComplete="off"
                                />
                                <small>Your secret passphrase. Never share this.</small>
                            </>
                        )}
                    </div>

                    <div className="form-section">
                        <label>Alias (OPTIONAL)</label>
                        <input
                            type="text"
                            name="alias"
                            value={formData.alias}
                            onChange={handleChange}
                            placeholder="Layman1, Author5, etc..."
                            maxLength="30"
                        />
                        <small>Leave blank for auto-generated "Layman-ID"</small>
                    </div>

                    <div className="form-divider"></div>

                    <div className="form-section">
                        <PublishDestination
                            value={formData.destination}
                            onChange={handleDestinationChange}
                            disabled={loading}
                            hint={isVideoDestination
                                ? 'Another creator\'s video we vouch for, paired with your commentary. It is listed on /videos, not in the article listing.'
                                : 'A written document, listed with the articles on the front page.'}
                        />
                    </div>

                    {isVideoDestination && (
                        <div className="form-section">
                            <VideoLinkField
                                value={formData.videoUrl}
                                onChange={handleVideoUrlChange}
                                onPreview={handleVideoPreview}
                                disabled={loading}
                                required
                            />
                        </div>
                    )}

                    <div className="form-section">
                        <label>DOCUMENT TITLE *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter the document title..."
                            required
                            maxLength="200"
                        />
                    </div>

                    <div className="form-section">
                        <label>DESCRIPTION *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Brief summary visible in the feed..."
                            required
                            maxLength="1000"
                            rows="4"
                        />
                        <small>{formData.description.length}/1000 characters</small>
                    </div>

                    <div className="form-section">
                        <ThumbnailPicker
                            value={formData.thumbnail}
                            onChange={handleThumbnailChange}
                            onUploadImage={handleMediaUpload}
                            label="THUMBNAIL (OPTIONAL)"
                            hint={isVideoDestination
                                ? 'Shown on the video\'s card on the Videos page. Leave it empty to use the video\'s own thumbnail.'
                                : 'This image appears above the title on the home page.'}
                            aspect={16 / 9}
                            suggestions={extractThumbnailSuggestions(formData.content)}
                        />
                    </div>

                    <div className="form-section">
                        <label>FULL CONTENT *</label>
                        <RichTextEditor
                            className="writer-publish-editor"
                            value={formData.content}
                            onChange={handleContentChange}
                            placeholder="Full document content"
                            onUploadImage={handleMediaUpload}
                        />
                        <small>Add photos, video and audio with the toolbar — pick several files at once, paste them, or drag and drop them into the text. Large photos are downscaled automatically; each photo can be up to 10MB (JPEG, PNG, WebP, AVIF or GIF — iPhone HEIC photos need exporting as JPEG first). Videos get a poster frame automatically. YouTube and Rumble links can be embedded too.</small>
                    </div>

                    <div className="form-section">
                        <label>Bibliography / Sources (Optional)</label>
                        <RichTextEditor
                            className="writer-publish-editor writer-publish-editor--bibliography"
                            value={formData.bibliography}
                            onChange={handleBibliographyChange}
                            placeholder="List references, links, or related reading"
                            onUploadImage={handleMediaUpload}
                        />
                        <small>Share citations and further reading to contextualize the document.</small>
                    </div>

                    <div className="form-section">
                        <label>Main Tags &amp; Sub-Tags *</label>
                        <TagGroupInput
                            groups={tagGroups}
                            value={formData.tagGroups}
                            onChange={handleTagGroupsChange}
                            disabled={loading}
                            required
                        />
                        <small>Add sub-tags under one or more of the main tags.</small>
                    </div>

                    <div className="form-section">
                        <label>Confidence Score *</label>
                        <select
                            name="confidenceScoreRating"
                            value={formData.confidenceScoreRating}
                            onChange={handleChange}
                            required
                        >
                            <option value="1">1 - Speculative</option>
                            <option value="2">2 - Emerging Narrative</option>
                            <option value="3">3 - Evidence-Based Claim</option>
                            <option value="4">4 - Mainstream Facts with Alternative Framing</option>
                            <option value="5">5 - Established Consensus or Ideology</option>
                        </select>
                        <small>Rate the depth of confidence applied to this document.</small>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={resetForm}
                        >
                            Reset Form
                        </button>
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? '[TRANSMITTING...]' : (isVideoDestination ? '>> PUBLISH TO VIDEOS' : '>> PUBLISH DOCUMENT')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WriterPublish;
